/**
 * Application shell layout with sidebar navigation and top bar.
 *
 * The sidebar shows navigation links filtered by the current user's role.
 * The top bar shows the user's avatar, name, and a dropdown menu.
 */

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { coreApiMode } from '@/config/deployment';
import {
  LayoutDashboard,
  Server,
  Radar,
  ShieldAlert,
  FileText,
  Settings,
  Building2,
  BookOpen,
  Scale,
  LogOut,
  Menu,
  X,
  User as UserIcon,
  ChevronDown,
  Layers,
  Sparkles,
} from 'lucide-react';
import BrandMark from '@/components/common/BrandMark';
import BillingNotice from '@/components/common/BillingNotice';
import { useState, useRef, useEffect } from 'react';

/** A single navigation item. */
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  /** If set, only these roles see this link. Omit to show for all authenticated users. */
  roles?: string[];
}

/** Platform administration pages (platform administrators only), shown in their own sidebar section. */
const platformItems: NavItem[] = [
  { label: 'Organizations', path: '/platform/organizations', icon: <Building2 className="h-5 w-5" /> },
  { label: 'Plans', path: '/platform/plans', icon: <Layers className="h-5 w-5" /> },
  { label: 'AI Usage', path: '/platform/ai-usage', icon: <Sparkles className="h-5 w-5" /> },
];

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: 'Assets', path: '/assets', icon: <Server className="h-5 w-5" /> },
  { label: 'Scans', path: '/scans', icon: <Radar className="h-5 w-5" /> },
  { label: 'Findings', path: '/findings', icon: <ShieldAlert className="h-5 w-5" /> },
  { label: 'Reports', path: '/reports', icon: <FileText className="h-5 w-5" /> },
  { label: 'PCI Readiness', path: '/pci', icon: <ShieldAlert className="h-5 w-5" /> },
  { label: 'Audit Log', path: '/audit', icon: <FileText className="h-5 w-5" />,roles:['platform_admin','customer_admin','remediation_lead'] },
  { label: 'Documentation', path: '/docs', icon: <BookOpen className="h-5 w-5" /> },
  { label: 'Terms & Policies', path: '/policies', icon: <Scale className="h-5 w-5" /> },
  {
    label: 'Admin',
    path: '/admin',
    icon: <Settings className="h-5 w-5" />,
    roles: ['platform_admin'],
  },
  {
    label: 'Org Settings',
    path: '/org-settings',
    icon: <Building2 className="h-5 w-5" />,
    roles: ['platform_admin','customer_admin'],
  },
];

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /** Filter nav items by user role. */
  const visibleItems = navItems.filter(
    (item) => (coreApiMode ? ['/assets','/org-settings','/scans','/findings','/reports','/dashboard','/pci','/audit','/docs','/policies'].includes(item.path) : !['/pci','/audit','/policies'].includes(item.path)) && (!item.roles || (user && item.roles.includes(user.role))),
  );

  function handleLogout() {
    logout();
    navigate('/login');
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const avatarElement = user?.avatar_url ? (
    <img
      src={user.avatar_url}
      alt={user.full_name}
      className="h-8 w-8 rounded-full object-cover"
    />
  ) : (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
      {getInitials(user?.full_name ?? '?')}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-sidebar text-white transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo / brand */}
        <div data-testid="sidebar-brand" className="flex h-16 items-center gap-2 px-6">
          <BrandMark className="h-9 w-9 shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-xl font-bold tracking-tight leading-tight">VANTAGE</span>
            <span className="text-xs text-indigo-200">by Velosynt</span>
          </div>
          <button
            className="ml-auto rounded p-1 hover:bg-sidebar-hover lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation links */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-sidebar-active text-white'
                    : 'text-indigo-200 hover:bg-sidebar-hover hover:text-white'
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Platform administration, kept apart from the signed-in organization's own pages */}
        {coreApiMode && user?.role === 'platform_admin' && (
          <nav aria-label="Platform Admin" className="space-y-1 border-t border-white/10 px-3 pb-4 pt-3">
            <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-indigo-300">Platform Admin</p>
            {platformItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'bg-sidebar-active text-white' : 'text-indigo-200 hover:bg-sidebar-hover hover:text-white'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}

        {/* Organization name, above the separator and the user's profile */}
        {user?.tenant_name && (
          <div className="px-4 pb-3">
            <div className="text-base font-semibold text-white truncate" title={user.tenant_name}>{user.tenant_name}</div>
          </div>
        )}

        {/* User info at bottom — clickable to profile */}
        <div
          className="border-t border-indigo-800 px-4 py-4 cursor-pointer hover:bg-sidebar-hover transition-colors"
          onClick={() => { navigate('/profile'); setSidebarOpen(false); }}
        >
          <div className="flex items-center gap-3">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.full_name}
                className="h-9 w-9 rounded-full object-cover border border-indigo-600"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-700 text-sm font-bold text-indigo-200 border border-indigo-600">
                {getInitials(user?.full_name ?? '?')}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-indigo-100 truncate">
                {user?.full_name}
              </div>
              <div className="text-xs text-indigo-300 truncate">{user?.email}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-16 items-center justify-between border-b bg-white px-4 shadow-sm lg:px-8">
          <button
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="flex-1" />

          {/* User dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              aria-label="Account menu"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-100 transition-colors"
            >
              {avatarElement}
              <span className="hidden text-sm text-gray-700 sm:block">
                {user?.full_name}
              </span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border bg-white py-1 shadow-lg">
                <button
                  onClick={() => { navigate('/profile'); setDropdownOpen(false); }}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <UserIcon className="h-4 w-4" />
                  Profile
                </button>
                <hr className="my-1" />
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          {coreApiMode && <div role="status" className="mb-6 rounded-md bg-indigo-50 p-3 text-sm text-indigo-900">Cloudflare staging build in validation. Email delivery requires a configured provider; production and external authenticated scanner checks remain pending.</div>}
          <BillingNotice user={user} />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
